import { toRaw, watch, type WatchOptions } from "vue";
import {
  defaultFiltersState,
  type FilterContext,
  type FiltersState,
  useFiltersStore,
} from "@/_store/filters.module";
import { useCuiFilters, type UseFiltersConfig } from "@coronet/cui";
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";

interface UseFiltersOptions<K extends FilterContext> {
  context: K;
  onFilterChange: () => Promise<void> | void;
  searchKey?: keyof FiltersState[K] & string;
  searchMinLength?: number;
  debounceMs?: number;
}

/**
 * Builds a `UseFiltersConfig` object for the given filter context, wiring it to
 * the Pinia filters store so `useCuiFilters` can read and mutate the correct slice.
 *
 * @param context - The filter context key (e.g. `FilterContext.DEVICES`).
 * @param options - Optional overrides for `searchKey`, `searchMinLength`, and `debounceMs`.
 * @returns A fully configured `UseFiltersConfig` ready to pass to `useCuiFilters`.
 */
export function buildFiltersConfig<K extends FilterContext>(
  context: K,
  options?: Pick<UseFiltersConfig<FiltersState[K]>, "searchKey" | "searchMinLength" | "debounceMs">
): UseFiltersConfig<FiltersState[K]> {
  const filtersStore = useFiltersStore();
  return {
    initialFilters: cloneDeep(toRaw(filtersStore.filters[context])),
    defaultFilters: cloneDeep(defaultFiltersState[context]),
    setFilters: (v) => filtersStore.setFilters(context, v),
    resetFilters: () => filtersStore.resetFilters(context),
    ...options,
  };
}

export function useFilters<K extends FilterContext>(
  { context, onFilterChange, searchKey, searchMinLength, debounceMs }: UseFiltersOptions<K>,
  watchOptions: WatchOptions = { deep: true }
) {
  const filtersStore = useFiltersStore();

  const cuiFilters = useCuiFilters(
    buildFiltersConfig(context, {
      searchKey,
      searchMinLength,
      debounceMs,
    })
  );

  watch(
    () => filtersStore.filters[context],
    async (newVal, oldVal) => {
      if (!isEqual(newVal, oldVal)) {
        await onFilterChange();
      }
    },
    watchOptions
  );

  return {
    ...cuiFilters,
  };
}
